"use client"

import React, { useState, useRef, useCallback, useEffect } from 'react'
import { cn } from '../../lib/utils'
import { UIButton } from '../buttons/ui-button'
import { UITextarea } from '../forms/ui-textarea'
import { UICard } from '../cards/ui-card'
import { UIScrollArea } from '../layout/ui-scroll-area'
import { UITypography } from '../display/ui-typography'
import { UIBox } from '../layout/ui-box'
import {
  Send,
  Paperclip,
  Mic,
  Square,
  Loader2,
  Plus,
  X
} from 'lucide-react'

// 프롬프트 템플릿 인터페이스
export interface UIPromptTemplate {
  id: string
  title: string
  description?: string
  content: string
  category?: string
  icon?: React.ComponentType<{ className?: string }>
}

// 첨부파일 인터페이스
export interface UIPromptAttachment {
  id: string
  name: string
  type: 'image' | 'file' | 'audio'
  size?: number
  url?: string
  preview?: string
}

// 프롬프트 입력 컴포넌트 Props
export interface UIPromptInputProps {
  // 기본 설정
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  onSubmit: (prompt: string, attachments?: UIPromptAttachment[]) => Promise<void>

  // 고급 기능
  suggestions?: string[]
  templates?: UIPromptTemplate[]
  multiline?: boolean
  maxLength?: number
  minRows?: number
  maxRows?: number

  // 상태 관리
  isLoading?: boolean
  disabled?: boolean
  autoFocus?: boolean

  // UI 옵션
  variant?: 'default' | 'floating'
  size?: 'sm' | 'default' | 'lg'
  showSendButton?: boolean
  showAttachButton?: boolean
  showMicButton?: boolean
  showSuggestions?: boolean
  showTemplates?: boolean
  showCharCounter?: boolean

  // 첨부파일
  attachments?: UIPromptAttachment[]
  onAttachmentsChange?: (attachments: UIPromptAttachment[]) => void
  maxAttachments?: number
  acceptedFileTypes?: string[]

  // 음성 입력
  enableVoiceInput?: boolean
  isRecording?: boolean
  onStartRecording?: () => void
  onStopRecording?: () => void

  // 스타일링
  className?: string
  inputClassName?: string
  buttonClassName?: string

  // 접근성 및 i18n
  ariaLabel?: string
  locale?: 'ko' | 'en'

  // 고급 콜백
  onFocus?: () => void
  onBlur?: () => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  onTemplateSelect?: (template: UIPromptTemplate) => void
  onSuggestionSelect?: (suggestion: string) => void
}

/**
 * UIPromptInput - AI 대화를 위한 고급 프롬프트 입력 컴포넌트
 * shadcn.io/ai Prompt Input 컴포넌트 기반으로 구현
 */
export const UIPromptInput = React.forwardRef<HTMLTextAreaElement, UIPromptInputProps>(
  ({
    placeholder,
    value,
    onChange,
    onSubmit,
    suggestions = [],
    templates = [],
    multiline = true,
    maxLength = 4000,
    minRows = 1,
    maxRows = 10,
    isLoading = false,
    disabled = false,
    autoFocus = false,
    variant = 'default',
    size = 'default',
    showSendButton = true,
    showAttachButton = false,
    showMicButton = false,
    showSuggestions = true,
    showTemplates = false,
    showCharCounter = true,
    attachments = [],
    onAttachmentsChange,
    maxAttachments = 5,
    acceptedFileTypes = ['image/*', 'text/*'],
    enableVoiceInput = false,
    isRecording = false,
    onStartRecording,
    onStopRecording,
    className,
    inputClassName,
    buttonClassName,
    ariaLabel,
    locale = 'ko',
    onFocus,
    onBlur,
    onKeyDown,
    onTemplateSelect,
    onSuggestionSelect,
    ...props
  }, ref) => {
    const [internalValue, setInternalValue] = useState('')
    const [rows, setRows] = useState(minRows)
    const [showTemplatesPanel, setShowTemplatesPanel] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const currentValue = value !== undefined ? value : internalValue
    const isControlled = value !== undefined

    // 자동 크기 조절
    useEffect(() => {
      if (multiline) {
        const lines = currentValue.split('\n').length
        const newRows = Math.min(Math.max(lines, minRows), maxRows)
        setRows(newRows)
      }
    }, [currentValue, minRows, maxRows, multiline])

    // 값 변경 핸들러
    const handleChange = useCallback((newValue: string) => {
      if (newValue.length > maxLength) return

      if (!isControlled) {
        setInternalValue(newValue)
      }
      onChange?.(newValue)
    }, [isControlled, maxLength, onChange])

    // 제출 핸들러
    const handleSubmit = useCallback(async () => {
      if (!currentValue.trim() || isLoading || disabled) return

      try {
        await onSubmit(currentValue.trim(), attachments)
        if (!isControlled) {
          setInternalValue('')
        }
      } catch (error) {
        console.error('Failed to submit prompt:', error)
      }
    }, [currentValue, isLoading, disabled, onSubmit, attachments, isControlled])

    // 키보드 이벤트 핸들러
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      onKeyDown?.(e)

      if (e.key === 'Enter') {
        if (e.shiftKey || !multiline) {
          // Shift+Enter 또는 단일행 모드에서는 새 줄
          return
        } else {
          // Enter만 누르면 제출
          e.preventDefault()
          handleSubmit()
        }
      }
    }, [onKeyDown, multiline, handleSubmit])

    // 파일 업로드 핸들러
    const handleFileUpload = useCallback((files: FileList) => {
      if (!onAttachmentsChange) return

      const newAttachments: UIPromptAttachment[] = Array.from(files)
        .slice(0, maxAttachments - attachments.length)
        .map(file => ({
          id: Math.random().toString(36).substring(2, 11),
          name: file.name,
          type: file.type.startsWith('image/') ? 'image' : 'file',
          size: file.size,
          url: URL.createObjectURL(file),
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
        }))

      onAttachmentsChange([...attachments, ...newAttachments])
    }, [attachments, onAttachmentsChange, maxAttachments])

    // 첨부파일 제거
    const removeAttachment = useCallback((id: string) => {
      if (!onAttachmentsChange) return
      onAttachmentsChange(attachments.filter(att => att.id !== id))
    }, [attachments, onAttachmentsChange])

    // 제안 선택
    const handleSuggestionClick = useCallback((suggestion: string) => {
      handleChange(suggestion)
      onSuggestionSelect?.(suggestion)
    }, [handleChange, onSuggestionSelect])

    // 템플릿 선택
    const handleTemplateClick = useCallback((template: UIPromptTemplate) => {
      handleChange(template.content)
      setShowTemplatesPanel(false)
      onTemplateSelect?.(template)
    }, [handleChange, onTemplateSelect])

    // 음성 입력 토글
    const toggleRecording = useCallback(() => {
      if (isRecording) {
        onStopRecording?.()
      } else {
        onStartRecording?.()
      }
    }, [isRecording, onStartRecording, onStopRecording])

    // 스타일 변형
    const containerStyles = {
      default: "w-full",
      floating: "fixed bottom-4 left-4 right-4 max-w-2xl mx-auto z-50",
    }

    const sizeStyles = {
      sm: "text-sm",
      default: "",
      lg: "text-lg",
    }

    const canSubmit = currentValue.trim().length > 0 && !isLoading && !disabled

    return (
      <UIBox className={cn(containerStyles[variant], className)}>
        {/* 템플릿 패널 */}
        {showTemplatesPanel && showTemplates && templates.length > 0 && (
          <UICard className="mb-2 p-4">
            <UIBox className="flex items-center justify-between mb-3">
              <UITypography as="h4" variant="subtitle2" size="sm">
                {locale === 'ko' ? '템플릿' : 'Templates'}
              </UITypography>
              <UIButton
                variant="ghost"
                size="sm"
                onClick={() => setShowTemplatesPanel(false)}
              >
                <X className="w-4 h-4" />
              </UIButton>
            </UIBox>
            <UIScrollArea className="max-h-40">
              <UIBox className="space-y-2">
                {templates.map((template) => (
                  <UIButton
                    key={template.id}
                    onClick={() => handleTemplateClick(template)}
                    variant="ghost"
                    className="w-full justify-start p-2 h-auto"
                  >
                    <UIBox className="flex items-start gap-2">
                      {template.icon && <template.icon className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                      <UIBox className="flex-1 min-w-0">
                        <UITypography as="div" size="sm" weight="medium">{template.title}</UITypography>
                        {template.description && (
                          <UITypography as="div" variant="caption" color="muted" className="mt-1">
                            {template.description}
                          </UITypography>
                        )}
                      </UIBox>
                    </UIBox>
                  </UIButton>
                ))}
              </UIBox>
            </UIScrollArea>
          </UICard>
        )}

        {/* 첨부파일 미리보기 - Apple Glassmorphism */}
        {attachments.length > 0 && (
          <UIBox className="mb-3 flex flex-wrap gap-2">
            {attachments.map((attachment) => (
              <UIBox
                key={attachment.id}
                className={cn(
                  "relative inline-flex items-center gap-2 rounded-lg p-3 pr-9 transition-all duration-300 ease-out",
                  "bg-white/[0.015] border border-white/[0.08] shadow-sm shadow-black/[0.02]",
                  "dark:bg-white/[0.008] dark:border-white/[0.06] dark:shadow-black/[0.08]",
                  "hover:bg-white/[0.025] hover:shadow-md hover:scale-105"
                )}
              >
                {attachment.preview && (
                  <img
                    src={attachment.preview}
                    alt={attachment.name}
                    className="w-8 h-8 rounded object-cover"
                  />
                )}
                <UITypography
                  size="sm"
                  truncate
                  className="max-w-20 text-foreground/80 dark:text-foreground/90"
                >
                  {attachment.name}
                </UITypography>
                <UIButton
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "absolute top-1 right-1 h-6 w-6 p-0 transition-all duration-300 ease-out",
                    "hover:bg-destructive/10 hover:scale-110"
                  )}
                  onClick={() => removeAttachment(attachment.id)}
                >
                  <X className="w-3 h-3 text-foreground/60 hover:text-destructive" />
                </UIButton>
              </UIBox>
            ))}
          </UIBox>
        )}

        {/* 메인 입력 영역 - Apple Glassmorphism */}
        <UIBox className={cn(
          // Apple glassmorphism main card
          "relative rounded-xl transition-all duration-300 ease-out",
          "bg-white/[0.02] border border-white/[0.06] shadow-lg shadow-black/[0.02]",
          "dark:bg-white/[0.01] dark:border-white/[0.04] dark:shadow-black/10",
          // Enhanced floating variant
          variant === 'floating' && [
            "shadow-2xl shadow-black/[0.08] dark:shadow-black/20",
            "bg-white/[0.04] dark:bg-white/[0.02]",
            "border-white/[0.08] dark:border-white/[0.06]"
          ],
          // Focus-within effect
          "focus-within:shadow-xl focus-within:border-white/[0.10] dark:focus-within:border-white/[0.08]",
          "focus-within:bg-white/[0.03] dark:focus-within:bg-white/[0.015]"
        )}>
          <UIBox className="flex items-end gap-2 p-3">
            {/* 추가 기능 버튼들 (왼쪽) */}
            <UIBox className="flex items-center gap-1">
              {showAttachButton && (
                <>
                  <UIButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled}
                    className={buttonClassName}
                  >
                    <Paperclip className="w-4 h-4" />
                    <UITypography className="sr-only">
                      {locale === 'ko' ? '파일 첨부' : 'Attach file'}
                    </UITypography>
                  </UIButton>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={acceptedFileTypes.join(',')}
                    onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                    className="hidden"
                  />
                </>
              )}

              {showTemplates && templates.length > 0 && (
                <UIButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTemplatesPanel(!showTemplatesPanel)}
                  disabled={disabled}
                  className={buttonClassName}
                >
                  <Plus className="w-4 h-4" />
                  <UITypography className="sr-only">
                    {locale === 'ko' ? '템플릿' : 'Templates'}
                  </UITypography>
                </UIButton>
              )}

              {enableVoiceInput && showMicButton && (
                <UIButton
                  type="button"
                  variant={isRecording ? "destructive" : "ghost"}
                  size="sm"
                  onClick={toggleRecording}
                  disabled={disabled}
                  className={buttonClassName}
                >
                  {isRecording ? (
                    <Square className="w-4 h-4" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                  <span className="sr-only">
                    {isRecording
                      ? (locale === 'ko' ? '녹음 중지' : 'Stop recording')
                      : (locale === 'ko' ? '음성 입력' : 'Voice input')
                    }
                  </span>
                </UIButton>
              )}
            </UIBox>

            {/* 텍스트 입력 영역 */}
            <UIBox className="flex-1 min-w-0">
              <UITextarea
                ref={ref}
                value={currentValue}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}
                placeholder={placeholder || (locale === 'ko' ? 'AI에게 메시지를 입력하세요...' : 'Type a message to AI...')}
                disabled={disabled || isLoading}
                autoFocus={autoFocus}
                rows={multiline ? rows : 1}
                className={cn(
                  "min-h-0 border-0 p-0 shadow-none resize-none focus-visible:ring-0",
                  sizeStyles[size],
                  inputClassName
                )}
                style={{
                  minHeight: multiline ? `${minRows * 1.5}em` : '1.5em',
                  maxHeight: multiline ? `${maxRows * 1.5}em` : '1.5em'
                }}
                aria-label={ariaLabel || (locale === 'ko' ? 'AI 프롬프트 입력' : 'AI prompt input')}
                {...props}
              />

              {/* 문자 수 카운터 */}
              {showCharCounter && (
                <UITypography as="div" variant="caption" color="muted" align="right" className="mt-1">
                  {currentValue.length}/{maxLength}
                </UITypography>
              )}
            </UIBox>

            {/* 전송 버튼 */}
            {showSendButton && (
              <UIButton
                type="submit"
                size="sm"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={cn(
                  "shrink-0",
                  buttonClassName
                )}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <UITypography className="sr-only">
                  {locale === 'ko' ? '전송' : 'Send'}
                </UITypography>
              </UIButton>
            )}
          </UIBox>

          {/* 키보드 단축키 힌트 */}
          {/* {multiline && (
            <UITypography as="div" variant="caption" color="muted" align="right" className="px-3 pb-2">
              <CornerDownLeft className="w-3 h-3 inline mr-1" />
              {locale === 'ko' ? '전송' : 'Send'} · Shift+
              <CornerDownLeft className="w-3 h-3 inline mx-1" />
              {locale === 'ko' ? '새 줄' : 'New line'}
            </UITypography>
          )} */}
        </UIBox>

        {/* 제안 - Apple Glassmorphism Pills */}
        {showSuggestions && suggestions.length > 0 && !currentValue && (
          <UIBox className="mt-3">
            <UIBox className="flex flex-wrap gap-2">
              {suggestions.slice(0, 4).map((suggestion, index) => (
                <UIButton
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSuggestionClick(suggestion)}
                  disabled={disabled}
                  className={cn(
                    "h-auto py-2 px-3 rounded-full transition-all duration-300 ease-out",
                    "bg-white/[0.015] border-white/[0.08] shadow-sm shadow-black/[0.02]",
                    "dark:bg-white/[0.008] dark:border-white/[0.06] dark:shadow-black/[0.08]",
                    "hover:bg-white/[0.025] hover:border-white/[0.12] hover:shadow-md",
                    "hover:scale-105 hover:-translate-y-0.5"
                  )}
                >
                  <UITypography
                    as="span"
                    variant="caption"
                    className="text-foreground/70 dark:text-foreground/80"
                  >
                    {suggestion}
                  </UITypography>
                </UIButton>
              ))}
            </UIBox>
          </UIBox>
        )}
      </UIBox>
    )
  }
)

UIPromptInput.displayName = "UIPromptInput"